/**
 * Utilitários de Segurança
 * Funções para sanitização de dados e proteção contra ataques XSS
 */

/**
 * Sanitiza HTML para prevenir ataques XSS (versão nativa)
 * @param dirty - HTML não confiável
 * @returns HTML sanitizado e seguro
 */
export const sanitizeHTML = (dirty: string): string => {
  if (!dirty) return '';

  // Criar elemento temporário para sanitização
  const div = document.createElement('div');
  div.textContent = dirty;
  return div.innerHTML;
};

/**
 * Sanitiza texto simples removendo caracteres perigosos
 * @param text - Texto não confiável
 * @returns Texto sanitizado
 */
export const sanitizeText = (text: string): string => {
  if (!text) return '';

  // Remove scripts, tags HTML e caracteres especiais perigosos
  return text
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .trim();
};

/**
 * Sanitiza entrada de usuário para uso em atributos HTML
 * @param input - Entrada do usuário
 * @returns String segura para atributos HTML
 */
export const sanitizeAttribute = (input: string): string => {
  if (!input) return '';

  return input
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\//g, '&#x2F;');
};

/**
 * Valida e sanitiza URL
 * @param url - URL não confiável
 * @returns URL segura ou null se inválida
 */
export const sanitizeURL = (url: string): string | null => {
  if (!url) return null;

  try {
    const urlObj = new URL(url);

    // Permitir apenas protocolos seguros
    const allowedProtocols = ['http:', 'https:', 'mailto:', 'tel:'];
    if (!allowedProtocols.includes(urlObj.protocol)) {
      return null;
    }

    // Verificar se não é script ou data URL
    const scriptProto = 'javascript:';
    if (url.toLowerCase().includes(scriptProto) || url.toLowerCase().includes('data:')) {
      return null;
    }

    return urlObj.href;
  } catch {
    return null; // URL inválida
  }
};

/**
 * Sanitiza objeto completo recursivamente
 * @param obj - Objeto com dados não confiáveis
 * @returns Objeto sanitizado
 */
export const sanitizeObject = <T extends Record<string, unknown>>(obj: T): T => {
  if (!obj || typeof obj !== 'object') return obj;

  const sanitized = (Array.isArray(obj) ? [] : {}) as T;

  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const value = obj[key];

      if (typeof value === 'string') {
        (sanitized as Record<string, unknown>)[key] = sanitizeText(value);
      } else if (typeof value === 'object' && value !== null) {
        (sanitized as Record<string, unknown>)[key] = sanitizeObject(
          value as Record<string, unknown>
        );
      } else {
        (sanitized as Record<string, unknown>)[key] = value;
      }
    }
  }

  return sanitized;
};

/**
 * Validação de email segura
 * @param email - Email para validar
 * @returns true se email é válido e seguro
 */
export const isValidEmail = (email: string): boolean => {
  if (!email) return false;

  // Regex seguro para email
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  // Verificar formato
  if (!emailRegex.test(email)) return false;

  // Verificar se não contém scripts
  if (email.includes('<') || email.includes('>') || email.includes('script')) {
    return false;
  }

  return true;
};

/**
 * Validação de telefone segura
 * @param phone - Telefone para validar
 * @returns true se telefone é válido e seguro
 */
export const isValidPhone = (phone: string): boolean => {
  if (!phone) return false;

  // Remover caracteres de formatação
  const cleaned = phone.replace(/[\s\-()]/g, '');

  // Verificar se contém apenas números e +
  const phoneRegex = /^\+?[0-9]{10,15}$/;

  return phoneRegex.test(cleaned);
};

/**
 * Escapa caracteres especiais para uso em RegExp
 * @param string - String para escapar
 * @returns String com caracteres especiais escapados
 */
export const escapeRegExp = (string: string): string => {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
};

/**
 * Valida e sanitiza nome de arquivo
 * @param filename - Nome do arquivo
 * @returns Nome de arquivo seguro
 */
export const sanitizeFilename = (filename: string): string => {
  if (!filename) return '';

  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '_') // Substituir caracteres perigosos
    .replace(/\.{2,}/g, '.') // Remover .. (path traversal)
    .replace(/^\.+/, '') // Remover pontos no início
    .substring(0, 255); // Limitar tamanho
};
